import {
  BoxGeometry,
  CapsuleGeometry,
  CircleGeometry,
  CylinderGeometry,
  Group,
  LatheGeometry,
  type Material,
  Mesh,
  PlaneGeometry,
  PointLight,
  RingGeometry,
  SphereGeometry,
  TorusGeometry,
  Vector2,
} from 'three';
import { EMIT, LIGHTS } from '../render/palette';
import {
  MATERIALS,
  applyContactShading,
  emitter,
  seeded,
  signMaterial,
  tintVertexColor,
} from '../render/materials';
import { lightPool } from './props';

/**
 * THE THINGS THAT MAKE IT WRONG.
 *
 * The building was a correct mall. Shops down both sides, atrium up the middle,
 * service corridor behind, food court at the end — all of it plausible, all of
 * it dead straight, and that is exactly the note it kept hitting: a mall at
 * night, closed. The reference sheet is not a mall at night. It is a mall that
 * has stopped being for anyone, photographed by someone who is not supposed to
 * be there, and the difference is almost entirely made of objects that are the
 * wrong size, in the wrong place, or still running with nobody to run for.
 *
 * Four rules everything in here follows:
 *
 *   Scale is the cheapest surrealism. A seven-metre teddy bear is the same
 *   geometry as a toy; only the number changed, and the number is the joke.
 *
 *   Nothing is broken. Broken reads as post-apocalypse. Everything here is in
 *   working order and pointlessly on — a fountain that was drained on purpose,
 *   a ride still taking money, a queue barrier with nobody in the queue.
 *
 *   No bodies, no blood, no message scrawled on a wall. The unease has to come
 *   from ordinary commercial furniture behaving normally in an empty room.
 *
 *   It has to survive the dark. Every one of these either catches the moon,
 *   sits in a pool, or emits — an unlit surreal object is just a dark lump.
 */

function part(geo: Parameters<typeof tintVertexColor>[0], mat: Material, tint: number): Mesh {
  applyContactShading(geo, { falloff: 1.4, strength: 0.24 });
  tintVertexColor(geo, tint);
  const m = new Mesh(geo, mat);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

function at<T extends Mesh | Group>(o: T, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): T {
  o.position.set(x, y, z);
  o.rotation.set(rx, ry, rz);
  return o;
}

// ---------------------------------------------------------------------------
// The bear
// ---------------------------------------------------------------------------

/**
 * THE BEAR ON THE ROOF.
 *
 * Sitting, hands in its lap, facing the car park. Seven metres.
 *
 * The whole thing turns on it being *soft*. A bear built out of the same
 * flat-shaded boxes as the air-conditioning plant it is sitting next to is just
 * more plant; the reason it lands is that it is the only object in the skyline
 * with no straight edges and no right angles in it, so it reads as fabric from
 * three hundred metres without a single texture. Hence spheres and capsules
 * throughout, deliberately non-uniformly scaled — a squashed sphere is stuffing
 * that has settled, a round one is a balloon.
 *
 * It is also strictly a face-forward object. The muzzle, the eyes and the ears
 * are the entire read at distance, and they all have to sit inside the head's
 * silhouette from the one angle anyone will ever see it: from below, from the
 * south. Everything behind the ears is unmodelled on purpose.
 *
 * `height` is the seated height, base to the top of the head — not the standing
 * height, which is roughly half again as much.
 */
export function giantBear(height = 7): Group {
  const g = new Group();
  // One scale factor for the whole animal, derived from the seated height, so
  // the proportions are fixed and only the number in the level file changes.
  const u = height / 7;
  const fur = MATERIALS.velvet();
  /**
   * Light for a brown bear, and the reason is that it is never seen in daylight.
   *
   * The honest colour of this thing is about 0x9c7a4e, and at that value it
   * came out of the renderer as a black cut-out from every camera in the game —
   * a dark, matte, roughness-0.98 surface lit by two floods and a moon simply
   * does not have the albedo to survive. The object has exactly one job, which
   * is to be visible on a roofline at a hundred metres, and losing a shade of
   * accuracy to keep that is not a close call.
   */
  const FUR = 0xc19560;
  const FUR_DARK = 0x9d7845;
  const PAD = 0xdec4a0;

  // Haunches. Two flattened spheres set wide — this is the widest part of the
  // silhouette and it is what makes it read as sitting rather than standing.
  for (const s of [-1, 1]) {
    const h = part(new SphereGeometry(1.55 * u, 10, 8), fur, FUR_DARK);
    h.scale.set(1, 0.78, 1.15);
    g.add(at(h, s * 1.15 * u, 1.2 * u, 0.1 * u));
  }

  // Legs out in front, soles toward the viewer. A seated plush's feet are the
  // second-biggest shape it has and they face the camera.
  for (const s of [-1, 1]) {
    const leg = part(new CapsuleGeometry(0.85 * u, 1.3 * u, 4, 10), fur, FUR);
    leg.scale.set(1, 1, 0.95);
    g.add(at(leg, s * 1.4 * u, 0.95 * u, 1.35 * u, Math.PI / 2 - 0.22, 0, s * 0.12));
    const foot = part(new SphereGeometry(0.95 * u, 10, 8), fur, FUR);
    foot.scale.set(1, 1.05, 0.72);
    g.add(at(foot, s * 1.5 * u, 1.0 * u, 2.5 * u));
    // Sole pad, a shade lighter, plus three toe beans. The single most
    // recognisable plush-toy detail there is.
    const sole = part(new CircleGeometry(0.6 * u, 12), MATERIALS.rubber(), PAD);
    g.add(at(sole, s * 1.5 * u, 1.0 * u, 3.16 * u));
    for (let i = 0; i < 3; i++) {
      const bean = part(new SphereGeometry(0.17 * u, 7, 6), MATERIALS.rubber(), PAD);
      bean.scale.set(1, 1, 0.5);
      g.add(at(bean, s * 1.5 * u + (i - 1) * 0.36 * u, 1.62 * u, 3.14 * u));
    }
  }

  // Belly. Pear-shaped, heavier at the bottom, because gravity is the thing
  // that separates a plush toy from a bear.
  const belly = part(new SphereGeometry(2.05 * u, 12, 10), fur, FUR);
  belly.scale.set(1, 1.02, 0.9);
  g.add(at(belly, 0, 2.75 * u, 0.15 * u));
  const chest = part(new SphereGeometry(1.55 * u, 10, 9), fur, FUR);
  chest.scale.set(1.02, 0.9, 0.88);
  g.add(at(chest, 0, 4.15 * u, 0.05 * u));
  // Lighter fur panel down the front, which is what stops the body being one
  // undifferentiated mass at range.
  const bib = part(new SphereGeometry(1.35 * u, 10, 8), fur, 0xc2a271);
  bib.scale.set(0.86, 1.18, 0.5);
  g.add(at(bib, 0, 3.15 * u, 1.35 * u));

  // Arms, resting in the lap, paws almost touching. A plush with its arms out
  // is waving; a plush with its arms down is waiting.
  for (const s of [-1, 1]) {
    const arm = part(new CapsuleGeometry(0.62 * u, 1.9 * u, 4, 10), fur, FUR);
    g.add(at(arm, s * 2.0 * u, 3.5 * u, 0.55 * u, 0.75, 0, s * 0.42));
    const paw = part(new SphereGeometry(0.72 * u, 9, 7), fur, FUR);
    paw.scale.set(1, 0.9, 1.05);
    g.add(at(paw, s * 1.05 * u, 2.35 * u, 1.6 * u));
    const pad = part(new CircleGeometry(0.4 * u, 10), MATERIALS.rubber(), PAD);
    g.add(at(pad, s * 1.05 * u, 2.35 * u, 2.32 * u, -0.12, 0, 0));
  }

  // Head. Slightly oversized against the body — an accurate bear head on this
  // body is a bear; an oversized one is a toy, and a toy is the point.
  const head = part(new SphereGeometry(1.85 * u, 12, 10), fur, FUR);
  head.scale.set(1, 0.95, 0.95);
  g.add(at(head, 0, 5.75 * u, 0.05 * u));

  // Ears, flat discs on the sides of the crown rather than spheres on top.
  for (const s of [-1, 1]) {
    const ear = part(new CylinderGeometry(0.62 * u, 0.62 * u, 0.34 * u, 12), fur, FUR);
    g.add(at(ear, s * 1.32 * u, 6.85 * u, -0.1 * u, 0, 0, Math.PI / 2 - s * 0.28));
    const inner = part(new CylinderGeometry(0.4 * u, 0.4 * u, 0.36 * u, 12), fur, 0xc2a271);
    g.add(at(inner, s * 1.38 * u, 6.87 * u, 0.02 * u, 0, 0, Math.PI / 2 - s * 0.28));
  }

  // Muzzle, nose, mouth.
  const muzzle = part(new SphereGeometry(0.86 * u, 10, 8), fur, 0xc2a271);
  muzzle.scale.set(1.15, 0.82, 0.9);
  g.add(at(muzzle, 0, 5.35 * u, 1.5 * u));
  const nose = part(new SphereGeometry(0.3 * u, 8, 6), MATERIALS.gloss(), 0x2b2620);
  nose.scale.set(1.25, 0.85, 0.8);
  g.add(at(nose, 0, 5.6 * u, 2.2 * u));
  g.add(at(part(new TorusGeometry(0.24 * u, 0.05 * u, 4, 10, Math.PI), MATERIALS.gloss(), 0x2b2620), 0, 5.18 * u, 2.16 * u, 0, 0, Math.PI));

  /**
   * The eyes, and the only part of this that is doing horror.
   *
   * Glass buttons. They are `gloss` and near-black, which in a scene lit by one
   * moon and two floods means they hold a single hard specular each — so as the
   * player crosses the car park the highlights slide across them and the bear
   * appears to track you. That is a real optical effect of a curved specular
   * surface, not a script, and it costs nothing. It is also the reason the eyes
   * are spheres proud of the face rather than painted discs.
   */
  for (const s of [-1, 1]) {
    const eye = part(new SphereGeometry(0.28 * u, 10, 8), MATERIALS.gloss(), 0x14100c);
    g.add(at(eye, s * 0.72 * u, 5.95 * u, 1.42 * u));
    // A tiny rim of stitching so the eye is set into the face, not stuck on it.
    g.add(at(part(new TorusGeometry(0.29 * u, 0.045 * u, 4, 10), fur, FUR_DARK), s * 0.72 * u, 5.95 * u, 1.4 * u));
  }

  // A seam up the middle of the head, and stitched joins at the shoulders.
  // Three cylinders' worth of "this was sewn" is what keeps it plush rather
  // than inflatable.
  for (const s of [-1, 1]) {
    g.add(at(part(new TorusGeometry(0.68 * u, 0.05 * u, 4, 12), fur, FUR_DARK), s * 1.6 * u, 4.05 * u, 0.15 * u, 0, s * 0.5, 0));
  }

  // The ribbon. A bow is the single clearest signal that this object was made
  // to be given to a child, which is the whole reason it is unsettling at the
  // scale of a bus, on a roof, in the dark.
  const collar = part(new TorusGeometry(1.5 * u, 0.19 * u, 6, 16), MATERIALS.velvet(), 0x7a2733);
  collar.scale.set(1, 1, 0.85);
  g.add(at(collar, 0, 4.75 * u, 0.15 * u, 1.35, 0, 0));
  for (const s of [-1, 1]) {
    const loop = part(new TorusGeometry(0.44 * u, 0.15 * u, 5, 12), MATERIALS.velvet(), 0x8b2f3c);
    loop.scale.set(1, 0.72, 0.5);
    g.add(at(loop, s * 0.62 * u, 4.9 * u, 1.35 * u, 0, s * 0.5, s * 0.5));
  }
  g.add(at(part(new SphereGeometry(0.22 * u, 7, 6), MATERIALS.velvet(), 0x93323f), 0, 4.9 * u, 1.4 * u));

  // Swing tag on the wrist. Deadpan, tiny, and only legible from the roof —
  // a reward for the player who climbs up here rather than a joke told loudly.
  const tag = new Mesh(
    new PlaneGeometry(0.62 * u, 0.42 * u),
    signMaterial({
      text: 'NOT FOR SALE',
      widthMetres: 0.62 * u,
      heightMetres: 0.42 * u,
      color: EMIT.torch,
      font: 'wide',
      pixelsPerMetre: 64,
    }),
  );
  g.add(at(tag, -1.35 * u, 2.1 * u, 1.9 * u, -0.25, -0.4, 0.2));

  return g;
}

// ---------------------------------------------------------------------------
// Interior beats
// ---------------------------------------------------------------------------

/**
 * A tiled fountain basin with no water in it.
 *
 * Drained, swept, and still lit from underneath — the uplighters are on, and
 * with nothing to refract through they just paint the empty bowl. Coins still
 * on the floor of it. This is the centrepiece of the atrium and it is the
 * single best-value liminal object in the building, because a dry fountain is
 * instantly legible as "closed indefinitely" in a way a locked shutter is not.
 */
export function drainedFountain(radius = 3.2): Group {
  const g = new Group();
  const wallH = 0.62;

  // Basin wall as a lathe, so it has a moulded coping rather than a box edge.
  const profile: Vector2[] = [
    new Vector2(radius - 0.42, 0),
    new Vector2(radius, 0),
    new Vector2(radius + 0.06, wallH - 0.14),
    new Vector2(radius + 0.02, wallH),
    new Vector2(radius - 0.34, wallH),
    new Vector2(radius - 0.4, wallH - 0.1),
    new Vector2(radius - 0.42, 0.14),
  ];
  const wall = part(new LatheGeometry(profile, 24), MATERIALS.tile(), 0x767e88);
  g.add(at(wall, 0, 0, 0));

  /**
   * A mosaic band round the outside, and it is doing more work than it looks.
   *
   * Standing on the concourse you are below the coping, so every detail inside
   * the basin — the coins, the drain, the sign, the whole reason the piece is
   * funny — is hidden, and the fountain reads as a smooth pale ring three metres
   * across sitting in the middle of the building's best view. This band is the
   * only thing the outside face has to say for itself: a deep teal course of the
   * kind every real centre put on one of these in 1994, broken into tesserae so
   * it has grain at two metres and still reads as a stripe at twenty.
   */
  const band = part(new CylinderGeometry(radius + 0.045, radius + 0.045, 0.2, 24, 1, true), MATERIALS.tile(), 0x2c5560);
  g.add(at(band, 0, wallH - 0.3, 0));
  for (let i = 0; i < 24; i++) {
    const a = (i / 24) * Math.PI * 2;
    const pip = part(new BoxGeometry(0.02, 0.2, 0.055), MATERIALS.tileDark(), 0x1d3941);
    g.add(at(pip, Math.cos(a) * (radius + 0.07), wallH - 0.3, Math.sin(a) * (radius + 0.07), 0, -a, 0));
    if (i % 3 === 0) {
      const accent = part(new BoxGeometry(0.02, 0.055, 0.24), MATERIALS.tile(), 0x8fb0a4);
      g.add(at(accent, Math.cos(a + 0.13) * (radius + 0.07), wallH - 0.3, Math.sin(a + 0.13) * (radius + 0.07), 0, -a, 0));
    }
  }

  // Floor of the basin, one step down and a dirtier tile.
  g.add(at(part(new CircleGeometry(radius - 0.4, 24), MATERIALS.tileDark(), 0x545b64), 0, 0.1, 0, -Math.PI / 2, 0, 0));
  // The drain, dead centre. Nothing sells "empty" like a visible plughole.
  g.add(at(part(new RingGeometry(0.12, 0.28, 12), MATERIALS.steelDark(), 0x4c5158), 0, 0.105, 0, -Math.PI / 2, 0, 0));

  // The tiered centrepiece, dry.
  const tiers: Array<[number, number, number]> = [
    [1.1, 0.34, 0.18],
    [0.72, 0.3, 0.72],
    [0.42, 0.26, 1.16],
  ];
  g.add(at(part(new CylinderGeometry(0.34, 0.5, 1.5, 12), MATERIALS.tile(), 0x8e949c), 0, 0.85, 0));
  for (const [r, h, y] of tiers) {
    g.add(at(part(new CylinderGeometry(r, r * 0.72, h, 16), MATERIALS.tile(), 0x9aa0a8), 0, 0.28 + y, 0));
    g.add(at(part(new CylinderGeometry(r - 0.1, r - 0.1, 0.04, 16), MATERIALS.tileDark(), 0x60676f), 0, 0.28 + y + h / 2, 0));
  }
  g.add(at(part(new CylinderGeometry(0.05, 0.05, 0.3, 6), MATERIALS.steel(), 0x8a929b), 0, 1.9, 0));

  /**
   * The uplighters, still running. Four submerged fittings pointed at a
   * centrepiece that has nothing to catch them, so the light lands on dry tile
   * and stops. The pool is deliberately small and inside the basin — the joke
   * only works if the fountain is lit and the floor around it is not.
   */
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + 0.4;
    const x = Math.cos(a) * (radius - 1.0);
    const z = Math.sin(a) * (radius - 1.0);
    g.add(at(part(new CylinderGeometry(0.14, 0.16, 0.1, 10), MATERIALS.steelDark(), 0x4c5158), x, 0.15, z));
    const lens = new Mesh(new CircleGeometry(0.11, 10), emitter('fountainLens', EMIT.fluoro, 2.6));
    g.add(at(lens, x, 0.205, z, -Math.PI / 2, 0, 0));
  }
  const wash = lightPool(radius * 0.9, EMIT.fluoro, 0.34, 'fountain');
  wash.position.y = 0.115;
  g.add(wash);
  // Up from 5.5/8/2. The tiers are the only thing in the basin tall enough to
  // be seen from the concourse and at the old value they were a black cone.
  const l = new PointLight(LIGHTS.fluoro.color, 11, 7, 1.6);
  l.position.set(0, 0.55, 0);
  g.add(l);

  // Coins on the tile. Thirty flat discs, seeded, clustered toward the middle,
  // because that is where people throw.
  const r = seeded(41);
  for (let i = 0; i < 30; i++) {
    const a = r() * Math.PI * 2;
    const d = Math.sqrt(r()) * (radius - 0.7);
    const coin = part(new CylinderGeometry(0.026, 0.026, 0.005, 8), MATERIALS.steel(), r() > 0.4 ? 0xb08d4a : 0x9aa2ab);
    g.add(at(coin, Math.cos(a) * d, 0.106, Math.sin(a) * d));
  }

  // The sign in the basin. Corporate, present tense, and completely useless.
  const notice = new Mesh(
    new PlaneGeometry(0.9, 0.44),
    signMaterial({
      text: 'PLEASE DO NOT\nENTER THE WATER',
      widthMetres: 0.9,
      heightMetres: 0.44,
      color: EMIT.signCyan,
      font: 'wide',
      pixelsPerMetre: 128,
    }),
  );
  g.add(at(notice, 0.4, 0.5, radius - 0.55, -0.35, 0, 0));
  g.add(at(part(new BoxGeometry(0.05, 0.5, 0.05), MATERIALS.steel(), 0x8a929b), 0.4, 0.3, radius - 0.5));
  return g;
}

/**
 * Coin-operated kiddie ride, still powered.
 *
 * A two-metre fibreglass animal on a plinth with a light on it and a speaker in
 * it, in an empty concourse at two in the morning. The reference sheet's whole
 * tone in one object: not frightening, not broken, just still switched on for
 * nobody. The audio layer can hook `userData.ride` if it wants a tinny loop.
 */
export function kiddieRide(): Group {
  const g = new Group();
  g.add(at(part(new BoxGeometry(1.15, 0.28, 1.7), MATERIALS.steelPainted(), 0x2f3a48), 0, 0.14, 0));
  g.add(at(part(new BoxGeometry(1.05, 0.1, 1.6), MATERIALS.rubber(), 0x22262b), 0, 0.31, 0));
  // Spring column.
  g.add(at(part(new CylinderGeometry(0.13, 0.16, 0.34, 10), MATERIALS.steelDark(), 0x4c5158), 0, 0.5, 0));

  // A vaguely equine body. Deliberately not a good sculpture — moulded plastic
  // animals are not good sculptures.
  const body = part(new CapsuleGeometry(0.32, 0.7, 4, 10), MATERIALS.gloss(), 0xd8c4b0);
  g.add(at(body, 0, 1.0, 0, Math.PI / 2, 0, 0));
  const neck = part(new CapsuleGeometry(0.18, 0.45, 4, 8), MATERIALS.gloss(), 0xd8c4b0);
  g.add(at(neck, 0, 1.28, 0.42, 0.6, 0, 0));
  const headM = part(new BoxGeometry(0.26, 0.28, 0.52), MATERIALS.gloss(), 0xd8c4b0);
  g.add(at(headM, 0, 1.6, 0.7, 0.3, 0, 0));
  for (const s of [-1, 1]) {
    g.add(at(part(new SphereGeometry(0.05, 6, 5), MATERIALS.gloss(), 0x14100c), s * 0.12, 1.66, 0.86));
    g.add(at(part(new CylinderGeometry(0.02, 0.05, 0.14, 5), MATERIALS.gloss(), 0xd8c4b0), s * 0.1, 1.76, 0.56));
    // Legs, tucked — it is mounted on a spring, it does not need to stand.
    g.add(at(part(new CapsuleGeometry(0.09, 0.34, 3, 6), MATERIALS.gloss(), 0xd8c4b0), s * 0.24, 0.72, 0.22, 0.4, 0, 0));
    g.add(at(part(new CapsuleGeometry(0.09, 0.3, 3, 6), MATERIALS.gloss(), 0xd8c4b0), s * 0.24, 0.74, -0.28, -0.4, 0, 0));
  }
  // Saddle, handle, mane.
  g.add(at(part(new BoxGeometry(0.4, 0.1, 0.5), MATERIALS.velvet(), 0x7a2733), 0, 1.3, -0.02));
  g.add(at(part(new TorusGeometry(0.16, 0.025, 4, 10, Math.PI), MATERIALS.steel(), 0x9aa2ab), 0, 1.36, 0.26, 0, 0, 0));
  for (let i = 0; i < 5; i++) {
    g.add(at(part(new BoxGeometry(0.03, 0.16, 0.08), MATERIALS.velvet(), 0x4a3320), 0, 1.5 - i * 0.02, 0.5 - i * 0.09, 0.5, 0, 0));
  }

  // Coin box, price panel, and the light that is on.
  g.add(at(part(new BoxGeometry(0.22, 0.34, 0.16), MATERIALS.steelPainted(), 0x2f3a48), 0.5, 0.6, 0.4));
  const price = new Mesh(
    new PlaneGeometry(0.2, 0.12),
    signMaterial({ text: '£1', widthMetres: 0.2, heightMetres: 0.12, color: EMIT.signAmber, font: 'wide' }),
  );
  g.add(at(price, 0.5, 0.68, 0.49));
  const marquee = new Mesh(
    new PlaneGeometry(1.0, 0.26),
    signMaterial({
      text: 'RIDE ALONG',
      widthMetres: 1.0,
      heightMetres: 0.26,
      color: EMIT.signMagenta,
      font: 'condensed',
      flicker: 0.35,
    }),
  );
  g.add(at(marquee, 0, 0.42, 0.86));
  g.add(lightPool(1.5, EMIT.signMagenta, 0.22, 'ride'));
  g.userData.ride = true;
  return g;
}

/**
 * A soft-play pen with the ball pit emptied and the balls in one net sack.
 *
 * The padding is the read: bright primary foam blocks are the only saturated
 * colour on the whole ground floor, and putting them in a room lit by one dying
 * fluorescent is what makes the palette do the work instead of the geometry.
 */
export function softPlay(w = 6, d = 5): Group {
  const g = new Group();
  const COLS = [0xc4553f, 0x3f7ac4, 0xc9a53f, 0x4aa06a];
  const r = seeded(19);

  // Padded floor, quartered like a gym mat.
  for (let i = 0; i < 4; i++) {
    const mat = part(new BoxGeometry(w / 2 - 0.06, 0.08, d / 2 - 0.06), MATERIALS.rubber(), COLS[i]);
    g.add(at(mat, ((i % 2) - 0.5) * (w / 2), 0.04, (Math.floor(i / 2) - 0.5) * (d / 2)));
  }
  // Padded perimeter posts and rails, open at the front.
  for (let i = 0; i <= 6; i++) {
    const x = -w / 2 + (w / 6) * i;
    g.add(at(part(new CylinderGeometry(0.1, 0.1, 0.9, 8), MATERIALS.rubber(), COLS[i % 4]), x, 0.45, -d / 2));
  }
  for (const s of [-1, 1]) {
    for (let i = 0; i <= 4; i++) {
      const z = -d / 2 + (d / 4) * i;
      if (s > 0 && i > 2) continue;
      g.add(at(part(new CylinderGeometry(0.1, 0.1, 0.9, 8), MATERIALS.rubber(), COLS[(i + 2) % 4]), (s * w) / 2, 0.45, z));
    }
  }
  g.add(at(part(new BoxGeometry(w, 0.14, 0.14), MATERIALS.rubber(), 0x3f7ac4), 0, 0.86, -d / 2));

  // The empty pit: a shallow depression with a lip, and nothing in it.
  g.add(at(part(new BoxGeometry(w * 0.5, 0.36, d * 0.42), MATERIALS.rubber(), 0xc4553f), 0, 0.18, 0.4));
  g.add(at(part(new BoxGeometry(w * 0.5 - 0.5, 0.06, d * 0.42 - 0.5), MATERIALS.rubber(), 0x8e6d55), 0, 0.37, 0.4));

  // The balls, bagged. A net sack of a hundred plastic balls in the corner is
  // more unsettling than a pit full of them, because somebody did that on
  // purpose and then left.
  const sack = new Group();
  for (let i = 0; i < 34; i++) {
    const b = part(new SphereGeometry(0.11, 6, 5), MATERIALS.gloss(), COLS[Math.floor(r() * 4)]);
    const a = r() * Math.PI * 2;
    const rad = Math.sqrt(r()) * 0.5;
    at(b, Math.cos(a) * rad, 0.14 + r() * 0.6, Math.sin(a) * rad * 0.9);
    sack.add(b);
  }
  g.add(at(sack, w / 2 - 0.9, 0, -d / 2 + 0.9));

  // The one light, and it is not a good one.
  const tube = new Mesh(new PlaneGeometry(1.6, 0.14), emitter('softPlayTube', EMIT.fluoro, 1.5));
  tube.rotation.x = Math.PI / 2;
  g.add(at(tube, 0, 2.9, 0, Math.PI / 2, 0, 0));
  g.add(lightPool(3.2, EMIT.fluoro, 0.16, 'softplay'));

  const sign = new Mesh(
    new PlaneGeometry(2.2, 0.5),
    signMaterial({
      text: 'UNDER 8s ONLY\nSHOES OFF',
      widthMetres: 2.2,
      heightMetres: 0.5,
      color: EMIT.signCyan,
      font: 'wide',
    }),
  );
  g.add(at(sign, 0, 1.5, -d / 2 - 0.08));
  return g;
}

/**
 * A retractable queue barrier, set up and going nowhere.
 *
 * Six posts, five belts, a switchback with no counter at the end of it. This is
 * pure liminality per triangle: it is the shape of a crowd with the crowd
 * removed, and it costs about forty triangles.
 */
export function queueMaze(): Group {
  const g = new Group();
  const posts: Array<[number, number]> = [
    [-1.6, -2.4],
    [1.6, -2.4],
    [1.6, -0.8],
    [-1.6, -0.8],
    [-1.6, 0.8],
    [1.6, 0.8],
    [1.6, 2.4],
  ];
  for (const [x, z] of posts) {
    g.add(at(part(new CylinderGeometry(0.16, 0.2, 0.05, 12), MATERIALS.steelDark(), 0x3b4048), x, 0.025, z));
    g.add(at(part(new CylinderGeometry(0.035, 0.035, 0.95, 8), MATERIALS.steel(), 0x9aa2ab), x, 0.5, z));
    g.add(at(part(new CylinderGeometry(0.06, 0.05, 0.1, 10), MATERIALS.steelDark(), 0x33383f), x, 1.0, z));
  }
  for (let i = 0; i < posts.length - 1; i++) {
    const [x0, z0] = posts[i];
    const [x1, z1] = posts[i + 1];
    const len = Math.hypot(x1 - x0, z1 - z0);
    const belt = part(new BoxGeometry(len, 0.05, 0.012), MATERIALS.velvet(), 0x1f2933);
    at(belt, (x0 + x1) / 2, 0.9, (z0 + z1) / 2, 0, -Math.atan2(z1 - z0, x1 - x0), 0);
    // Belts sag. A dead-straight one reads as a wire, and a wire is a wall.
    belt.scale.set(1, 1, 1);
    g.add(belt);
  }
  return g;
}

/**
 * One mylar balloon on a ribbon, caught against the ceiling.
 *
 * The smallest object in the file and possibly the best one. It is a bright
 * specular disc floating six metres up in a black atrium — the eye finds it
 * immediately, spends a second working out what it is, and comes away with the
 * information that there was a party here at some point and it is over.
 */
export function strayBalloon(dropLength = 2.4, tint = 0xc8cfd8): Group {
  const g = new Group();
  const skin = part(new SphereGeometry(0.42, 12, 10), MATERIALS.gloss(), tint);
  skin.scale.set(1, 0.95, 0.72);
  g.add(at(skin, 0, -0.42, 0));
  // The crimped neck and the knot.
  g.add(at(part(new CylinderGeometry(0.05, 0.09, 0.12, 8), MATERIALS.gloss(), tint), 0, -0.86, 0));
  g.add(at(part(new SphereGeometry(0.05, 6, 5), MATERIALS.gloss(), 0xe6e9ee), 0, -0.93, 0));
  // Ribbon, hanging with a slight lean so it does not read as a rod.
  const ribbon = part(new CylinderGeometry(0.006, 0.006, dropLength, 4), MATERIALS.velvet(), 0xb9c4cf);
  g.add(at(ribbon, 0.08, -0.95 - dropLength / 2, 0.04, 0.03, 0, -0.07));
  return g;
}

/**
 * A single escalator step-plate section, on its side, wrapped and labelled.
 *
 * Maintenance in progress, forever. Belongs at the foot of a stopped escalator
 * — the object that explains why the building is like this, which is more
 * unsettling than no explanation, because the explanation is boring.
 */
export function maintenanceHoarding(w = 3.2, h = 2.0): Group {
  const g = new Group();
  for (const s of [-1, 1]) {
    g.add(at(part(new BoxGeometry(0.08, h, 0.5), MATERIALS.timber(), 0x6b5a41), (s * (w - 0.2)) / 2, h / 2, 0));
  }
  const board = part(new BoxGeometry(w, h, 0.06), MATERIALS.card(), 0xb9b2a2);
  g.add(at(board, 0, h / 2, 0));
  // Hazard banding along the bottom, which is the only bit anyone reads.
  for (let i = 0; i < Math.floor(w / 0.3); i++) {
    g.add(at(part(new BoxGeometry(0.2, 0.24, 0.02), MATERIALS.card(), i % 2 ? 0xd4a03f : 0x2b2f36), -w / 2 + 0.2 + i * 0.3, 0.2, 0.04, 0, 0, 0.5));
  }
  const notice = new Mesh(
    new PlaneGeometry(w * 0.8, h * 0.4),
    signMaterial({
      text: 'IMPROVEMENTS\nIN PROGRESS\nSORRY FOR ANY\nINCONVENIENCE',
      widthMetres: w * 0.8,
      heightMetres: h * 0.4,
      color: EMIT.signCyan,
      font: 'wide',
      pixelsPerMetre: 96,
    }),
  );
  g.add(at(notice, 0, h * 0.62, 0.05));
  return g;
}
